// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import {
  Icon, Typography, Button, TextField, Chip,
  ThemeProvider, TableFooter, TableRow, TableCell,
  createMuiTheme,
} from '@material-ui/core';
import MaterialTable, { MTableBody } from 'material-table';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Moment from 'react-moment';
import LaunchIcon from '@material-ui/icons/Launch';
import { connect } from 'react-redux';
import { Modal } from 'reactstrap';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import _ from 'lodash';
import { Autocomplete } from '@material-ui/lab';

// Internal Dependencies
import { IfTrueThrowError, IsArray, IsEmpty } from '../../common/checks';
import { Sleep, Trim } from '../../common/util';
import Loading from '../global/Loading';
import apiService from '../../services.js/apiService';
import Colours from '../../styles/colours';
import { updateModalContent, toggleModal } from '../../actions/modal';
import Tagging from '../common/tagging';
import LinkPageOrLocation from './linkPageOrLocation';
import Constants, { Status } from '../../common/constants';
import RadioBtnSelect from '../common/radioBtnSelect';
import MaterialDatePicker from '../global/form/MaterialDatePicker';
import Photos from './photos';

// commented because relate to videos part
// import Videos from './videos';

const themeTable = createMuiTheme({
  overrides: {
    MuiTableRow: {
      root: {
        '&:hover, &:nth-child(odd):hover': {
          transition:      '0.3s ease',
          backgroundColor: '#ffecb3',
        },
        '&:nth-child(odd)': {
          backgroundColor: '#eeeeee',
        },
      },
    },
  },
});

const ReviewNew = (props) => {
  const tableRef = useRef(null);
  const [review, setReview] = useState(null);

  const columns = [
    {
      title:     'Page/Location',
      filtering: false,
      width:     '3%',
      render:    (placesAndLocations) => (
        !IsEmpty(placesAndLocations.placePageId) ? <span>Linked to a Page</span> : <span>Custom Location</span>
      ),
    },
    {
      title:     'Page ID',
      field:     'placePageId',
      filtering: false,
      width:     '5%',
    },
    {
      title:     'Page Name',
      field:     'name', filtering: false,
      width:     '5%',
      render:    (placesAndLocations) => (
        !IsEmpty(placesAndLocations.placePageId) ? <span>{placesAndLocations.name}</span> : <span>{' '}</span>
      ),
    },
    {
      title:     'Applicable Outlets',
      field:     'isAllOutlets',
      filtering: false,
      render:    (placesAndLocations) => (
        placesAndLocations.isAllOutlets === false ? (
          <span>
            {placesAndLocations.outlets.length}
            {' '}
            outlet(s) selected
          </span>
        )
          : placesAndLocations.isAllOutlets === true ? <span>Review is for all outlets</span>
            : <span>{' '}</span>
      ),
    },
    {
      title:     'Place Name',
      field:     'name',
      filtering: false,
      width:     '5%',
      render:    (placesAndLocations) => (
        !IsEmpty(placesAndLocations.placePageId) ? <span>{' '}</span> : <span>{placesAndLocations.name}</span>
      ),
    },
    {
      title:     'Address',
      filtering: false,
      width:     '5%',
      render:    (placesAndLocations) => (
        !IsEmpty(placesAndLocations.placePageId) ? <span>{' '}</span>
          : !IsEmpty(placesAndLocations.isHomeBased) && IsEmpty(placesAndLocations.address) ? <span>{' '}</span>
            : (
              <span>
                {placesAndLocations.locatedIn}
                {' '}
                {placesAndLocations.address}
                {' '}
                {!IsEmpty(placesAndLocations.unitNo) && `#${placesAndLocations.unitNo}`}
                {' '}
                S(
                {placesAndLocations.postalCode}
                )
              </span>
            )
      ),
    },
    {
      title:     'Tags',
      field:     'tags',
      filtering: false,
      width:     '15%',
      render:    (placesAndLocations) => (
        <Autocomplete
          disabled
          limitTags={3}
          multiple
          disableCloseOnSelect
          disablePortal
          id="tags-outlined"
          options={review.tags}
          value={placesAndLocations.tags}
          renderTags={(value, getTagProps) => value.map((tag, tagIndex) => (
            <Chip
              {...getTagProps({ tagIndex })}
              key={tag || `tag-${tagIndex}`}
              variant="outlined"
              label={tag}
              className="mr-2 mb-2"
              style={{ borderColor: Colours.Black, fontSize: 12, background: Colours.Black, color: Colours.White }}
            />
          ))}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
            />
          )}
        />
      ),
    },
    {
      title:     'URL',
      field:     'url',
      filtering: false,
      width:     '5%',
      render:    (placesAndLocations) => (
        !IsEmpty(placesAndLocations.placePageId) ? <span>{' '}</span> : <span>{placesAndLocations.url}</span>
      ),
    },
    { title:     'Contact Info',
      field:     'contactInfo',
      filtering: false,
      width:     '5%',
      render:    (placesAndLocations) => (
        !IsEmpty(placesAndLocations.placePageId) ? <span>{' '}</span> : <span>{placesAndLocations.phoneNumber}</span>
      ),
    },
    {
      title:     'Rating',
      field:     'rating',
      filtering: false,
      width:     '5%',
    },
  ];

  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openLinkPageLocationModal, setOpenLinkPageLocationModal] = useState(false);
  const [placesAndLocations, setPlacesAndLocations] = useState([]);
  const [editPlaceOrLocation, setEditPlaceOrLocation] = useState(null);
  const [editPlaceLocationIndex, setEditPlaceOrLocationIndex] = useState(-1);

  const [pageSize, setPageSize] = useState(10);
  const [totalOutletCount, setTotalOutletCount] = useState(0);
  let outletCount = 0;

  const getOutletCounts = async (data) => {
    for (let i = 0; i < data.length; i += 1) {
      if ('placePageId' in data[i]) {
        if (data[i].isAllOutlets === true) {
          const outlets = await apiService.getPageOutlets(data[i].placePageId, { status: Status.Live });
          outletCount += outlets.length;
        } else {
          outletCount += data[i].outlets.length;
        }
      } else {
        outletCount += 1;
      }
    }
    setTotalOutletCount(outletCount);
  };

  const fetchReview = async (reviewId) => {
    try {
      setIsLoading(true);
      const data = await apiService.getReview(reviewId);
      setReview(data);
      const reviewPlaces = IsArray(data.reviewPlaces) ? data.reviewPlaces : [];
      const reviewLocations = IsArray(data.reviewLocations) ? data.reviewLocations : [];

      setPlacesAndLocations(_.concat(reviewPlaces, reviewLocations));
      getOutletCounts(_.concat(reviewPlaces, reviewLocations));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    try {
      const { reviewId } = props.match.params;
      if (IsEmpty(reviewId)) {
        throw new Error('No review id found in url');
      } else {
        fetchReview(reviewId);
      }
    } catch (err) {
      setIsLoading(false);
      toast.error(err.message);
    }
  }, []);

  const onRedirect = () => {
    if (history.length === 1) {
      window.close();
    } else if (_.has(history.location.state, 'from') && (history.location.state.from === 'page-review-listing' || history.location.state.from === 'page-add-review')) {
      history.push(`/admin/page/${props.match.params.pageId}/reviews`);
    } else {
      history.push('/admin/reviews');
    }
  };

  const updateReviewTagsWithCustomLocationOrLinkedPlaceTags = (locationOrPlaceTags = []) => {
    // we will just add new tags from custom location or linked place's tags to review tags
    const cuisineTags = _.filter(locationOrPlaceTags, (tag) => String(tag).endsWith('cuisine'));
    const otherTags = _.difference(locationOrPlaceTags, cuisineTags);

    const cuisineTagsToAddToReview = _.difference(cuisineTags, review.cuisineTags);
    const otherTagsToAddToReview = _.difference(otherTags, review.tags);

    const copyCuisineTags = review.cuisineTags.slice();
    for (const tag of cuisineTagsToAddToReview) {
      copyCuisineTags.push(tag);
    }

    const copyTags = review.tags.slice();
    for (const tag of otherTagsToAddToReview) {
      copyTags.push(tag);
    }

    setReview({ ...review, cuisineTags: copyCuisineTags, tags: copyTags });
  };

  const updateReviewTagsWithAllCustomLocationsAndLinkedPlacesTags = () => {
    // collate all tags from linked places' tags and custom locations' tags
    let allTags = [];
    for (const place of review.reviewPlaces) {
      allTags = _.concat(place.tags || [], allTags);
    }
    for (const location of review.reviewLocations) {
      allTags = _.concat(location.tags || [], allTags);
    }

    const cuisineTags = _.uniq(_.filter(allTags, (tag) => String(tag).endsWith('cuisine')));
    const tags = _.uniq(_.difference(allTags, cuisineTags));

    review.cuisineTags = cuisineTags;
    review.tags = tags;
    setReview({ ...review, cuisineTags, tags });
  };

  // Commented this function since we're not using it for bulk update the tags for custom locations with same name.
  // const bulkUpdateCustomLocationTagsByName = (name, tags) => {
  //   if (IsEmpty(review.reviewLocations)) {
  //     return;
  //   }

  //   let updateCount = 0;
  //   const reviewLocations = review.reviewLocations.slice();
  //   for (const location of reviewLocations) {
  //     if (location.name === name) {
  //       location.tags = tags;
  //       updateCount += 1;
  //     }
  //   }
  //   // since we are looping through all locations, we can be updating the same location again,
  //   // so only show toast if we are updating more than 1 locations.
  //   if (updateCount > 1) {
  //     setReview({ ...review, reviewLocations });
  //     toast.info(`Tags of all locations '${name}' has been auto-updated to the same tags.`);
  //   }
  // };

  const handleAddNewPlaceOrLocation = async (data) => {
    // if data has 'placePageId' => it is a Page; else it is a custom location
    try {
      if ('placePageId' in data) {
        if (IsEmpty(review.reviewPlaces)) {
          review.reviewPlaces = [];
        }
        review.reviewPlaces.push(data);
        if (data.isAllOutlets === false) {
          setTotalOutletCount(totalOutletCount + data.outlets.length);
        } else {
          const outlets = await apiService.getPageOutlets(data.placePageId, { status: Status.Live });
          setTotalOutletCount(totalOutletCount + outlets.length);
        }
      } else {
        if (IsEmpty(review.reviewLocations)) {
          review.reviewLocations = [];
        }
        review.reviewLocations.push(data);
        setTotalOutletCount(totalOutletCount + 1);
      }

      updateReviewTagsWithCustomLocationOrLinkedPlaceTags(data.tags || []);

      // Commented on this so it won't update all other custom locations' tags that has the same name as this
      // bulkUpdateCustomLocationTagsByName(data.name, data.tags);

      // update table
      placesAndLocations.push(data);

      // close modal
      setOpenLinkPageLocationModal(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdatePlaceOrLocation = (data) => {
    // if data has 'placePageId' => it is a Page; else it is a custom location
    try {
      if ('placePageId' in data) {
        const copy = review.reviewPlaces.slice();
        for (let i = 0; i < copy.length; i += 1) {
          if (data.placePageId === copy[i].placePageId) {
            // found
            copy[i] = data;
            break;
          }
        }
        review.reviewPlaces = copy;
      } else {
        const copy = review.reviewLocations.slice();
        const compareTempId = 'tempId' in data;
        for (let i = 0; i < copy.length; i += 1) {
          if ((compareTempId && data.tempId === copy[i].tempId) || (!compareTempId && data.id === copy[i].id)) {
            // found
            copy[i] = data;
            break;
          }
        }
        review.reviewLocations = copy;
      }

      updateReviewTagsWithAllCustomLocationsAndLinkedPlacesTags();

      // Commented this so that it won't update all other custom locations' tags that has the same name as this
      // bulkUpdateCustomLocationTagsByName(data.name, data.tags);

      // update table
      const tableCopy = placesAndLocations.slice();
      tableCopy[editPlaceLocationIndex] = data;
      setPlacesAndLocations(tableCopy);

      //to count total outlets after edit and update a location.
      getOutletCounts(tableCopy);

      // close modal
      setOpenLinkPageLocationModal(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteReviewPlace = (data) => {
    try {
      for (let index = 0; index < review.reviewPlaces.length; index += 1) {
        if (review.reviewPlaces[index].placePageId === data.placePageId) {
          review.reviewPlaces.splice(index, 1);
          break;
        }
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteReviewLocation = (data) => {
    try {
      for (let index = 0; index < review.reviewLocations.length; index += 1) {
        if (!IsEmpty(data.tempId) && data.tempId === review.reviewLocations[index].tempId) {
          review.reviewLocations.splice(index, 1);
          break;
        } else if (!IsEmpty(data.id) && data.id === review.reviewLocations[index].id) {
          review.reviewLocations.splice(index, 1);
          break;
        }
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deletePlaceOrLocation = async (data) => {
    try {
      const isReviewPlace = ('placePageId' in data);
      let indexToDel = -1;

      // eslint-disable-next-line guard-for-in
      for (let index = 0; index < placesAndLocations.length; index += 1) {
        const row = placesAndLocations[index];
        if (isReviewPlace) {
          if (row.placePageId === data.placePageId) {
            indexToDel = index;
            // found
            break;
          }
        } else if (IsEmpty(row.placePageId)) {
          if (!IsEmpty(data.id)) {
            // deleting a reviewLocation in db
            if (row.id === data.id) {
              indexToDel = index;
              break;
            }
          } else if (row.tempId === data.tempId) {
            indexToDel = index;
            break;
          }
        }
      }

      if (indexToDel === -1) {
        toast.error('unable to delete row due to invalid index');
        return;
      }
      // delete
      if (isReviewPlace) {
        deleteReviewPlace(data);
      } else {
        deleteReviewLocation(data);
      }

      await apiService.deleteReviewPlaceLocation(data.id, review.id, data);

      const copy = placesAndLocations.slice().filter((loc, index) => index !== indexToDel);

      setPlacesAndLocations(copy);
      updateReviewTagsWithAllCustomLocationsAndLinkedPlacesTags();

      toast.success(`Review Place/Location removed.`, { autoClose: 6000 });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const askDeleteConfirmation = (data) => {
    props.updateModalContent({
      title:        'Confirmation',
      content:      'Delete this record?',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          deletePlaceOrLocation(data);
          if ('placePageId' in data) {
            if (data.isAllOutlets === false) {
              setTotalOutletCount(totalOutletCount - data.outlets.length);
            } else {
              const outlets = await apiService.getPageOutlets(data.placePageId, { status: Status.Live });
              setTotalOutletCount(totalOutletCount - outlets.length);
            }
          } else {
            setTotalOutletCount(totalOutletCount - 1);
          }
        },
      },
      cancelText:       'Cancel',
      closeAfterAction: true,
    });
    props.toggleModal();
  };

  const validateReview = () => {
    const hasEmptyDisplay = review.images.some((val) => IsEmpty(val.display));
    IfTrueThrowError(!IsEmpty(review.images) && hasEmptyDisplay, 'Please crop all image(s) before saving the review');
    if (review.status === Status.Live) {
      IfTrueThrowError(IsEmpty(review.images) && IsEmpty(review.videos), 'Media is required');
      IfTrueThrowError(review.reviewDate === null, 'Review date is required');
      IfTrueThrowError(
        (IsEmpty(review.reviewPlaces) && IsEmpty(review.reviewLocations)),
        `At least one review's places or custom location is required to change review status to Live`,
      );
    } else if (review.status === Status.Rejected) {
      IfTrueThrowError(
        IsEmpty(Trim(review.remarks)),
        `Reject reason is mandatory if Reject status is selected`,
      );
    }
  };

  const onImageChange = (img) => {
    if (img !== review.image) {
      if (IsEmpty(img) && !IsEmpty(review.images)) {
        img = review.images[0].display;
      }
      setReview({ ...review, image: img });
    }
  };

  const onReviewImageChange = (images) => {
    if (images !== review.images) {
      review.images = images;
      setReview({ ...review });
    }
  };

  // we commented this since it is related to videos
  // const onReviewVideoChange = (videos) => {
  //   if (videos !== review.videos) {
  //     review.videos = videos;
  //     setReview({ ...review });
  //   }
  // };

  const updateReview = async () => {
    try {
      setIsSubmitting(true);
      updateReviewTagsWithAllCustomLocationsAndLinkedPlacesTags();
      await Sleep(200);
      validateReview();
      await apiService.updateReview(review.id, review);
      toast.success('Review Updated');
    } catch (error) {
      toast.error(error.message, { autoClose: 7000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid" style={{ backgroundColor: 'transparent' }}>
      <div className="row d-flex flex-row justify-content-center">
        <div className="col-12 col-lg-10 d-flex flex-row align-items-center">
          <Button onClick={onRedirect} size="large">
            <Icon fontSize="large">arrow_back</Icon>
          </Button>
          <Typography variant="h4" style={{ fontWeight: 'bold' }}>Edit Review</Typography>
        </div>
      </div>

      {!isLoading && IsEmpty(review) && (<Typography variant="h6">Review not found</Typography>)}

      {!isLoading && !IsEmpty(review) && (
        <div className="row d-flex flex-column align-items-center">
          <div className="col-12 col-lg-10 ml-4 row">
            <div className="col-12 d-flex flex-row justify-content-between">
              <div className="col-6 d-flex justify-content-between">
                <Typography variant="subtitle1" style={{ color: Colours.Grey }}>
                  ID:
                  {' '}
                  {review.id}
                </Typography>

                <Typography variant="subtitle1" style={{ color: Colours.Grey }}>
                  Review Status:
                  {' '}
                  {review.status === Constants.Status.Live ? <div className="badge badge-success p-2 pr-3 pl-3">{review.status}</div>
                    : review.status === Constants.Status.Draft || review.status === Constants.Status.Pending ? <div className="badge badge-warning p-2 pr-3 pl-3">{review.status}</div>
                      : <div className="badge badge-danger p-2 pr-3 pl-3">{review.status}</div>}

                </Typography>

                <Typography variant="subtitle1" style={{ color: Colours.Grey }}>
                  Searchable Status:
                  {' '}
                  {review.searchableStatus === Constants.Status.Live
                    ? <div className="badge badge-success p-2 pr-3 pl-3">{review.searchableStatus}</div>
                    : review.searchableStatus === Constants.Status.Pending
                      ? <div className="badge badge-warning p-2 pr-3 pl-3">{review.searchableStatus}</div>
                      : <div className="badge badge-danger p-2 pr-3 pl-3">{review.searchableStatus}</div>}
                </Typography>
              </div>

              <Typography variant="subtitle1" style={{ color: Colours.Grey }}>
                Last Updated:
                {' '}
                <span><Moment format="DD-MM-YYYY HH:mm">{review.updatedAt}</Moment></span>
              </Typography>
            </div>

            <div className="col-12 mt-4 row">
              <div className="col-12">
                <h3 className="d-block mb-5">Upload/Manage Media</h3>
                {/*
                 * We comment video part, because we will release image part at first
                Videos
                  reviewId={review.id}
                  videos={review.videos}
                  image={review.image}
                  onImageChange={(i) => onImageChange(i)}
                  onSortVideo={(sv) => onReviewVideoChange(sv)}
                  onReviewVideoChange={(video) => onReviewVideoChange(video)}
                  />*/}
                <Photos
                  reviewId={review.id}
                  image={review.image}
                  images={review.images}
                  onImageChange={(i) => onImageChange(i)}
                  onSortImage={(si) => onReviewImageChange(si)}
                  onReviewImageChange={(image) => onReviewImageChange(image)}
                />
              </div>
            </div>

            <div className="col-12 mt-4">
              <Typography variant="h6">Title</Typography>
              <TextField
                className="mt-2"
                value={review.title || ''}
                onChange={(e) => {
                  setReview({ ...review, title: e.target.value });
                }}
                variant="outlined"
                multiline
                size="small"
                fullWidth
              />
            </div>

            <div className="col-12 mt-4">
              <Typography variant="h6">Content</Typography>
              <TextField
                className="mt-2"
                value={review.content || ''}
                onChange={(e) => {
                  setReview({ ...review, content: e.target.value });
                }}
                variant="outlined"
                multiline
                rows={5}
                size="small"
                fullWidth
              />
            </div>

            <div className="col-12 mt-4">
              <Typography variant="h6" className="d-inline-block mr-4">Url</Typography>
              {(!IsEmpty(Trim(review.url)) && (String(review.url).startsWith('http://') || String(review.url).startsWith('https://')))
                ? (
                  <>
                    <Button
                      href={review.url}
                      target="_blank"
                      variant="outlined"
                      color="secondary"
                      style={{ border: `1px solid ${Colours.Primary}` }}
                      size="medium"
                      className="d-inline-block mb-2"
                    >
                      <span style={{ color: Colours.Primary, marginRight: 5 }}>Open Link</span>
                      <LaunchIcon style={{ color: Colours.Primary }} />
                    </Button>
                  </>
                ) : (!IsEmpty(Trim(review.url)) && (!String(review.url).startsWith('http://') || !String(review.url).startsWith('https://'))) ? (
                  <p style={{ color: 'red' }}>
                    Invalid url
                    <br />
                    <small style={{ color: 'gray' }}>Must start with http:// or https://</small>
                  </p>
                ) : ''}

              <TextField
                className="mt-2"
                value={review.url || ''}
                onChange={(e) => {
                  setReview({ ...review, url: e.target.value });
                }}
                variant="outlined"
                multiline
                size="small"
                fullWidth
              />
            </div>

            <div className="col-12 mt-4">
              <Typography variant="h6">Review Date</Typography>
              <MaterialDatePicker
                label=""
                clearable
                minDate={false}
                value={IsEmpty(review.reviewDate) ? null : new Date(review.reviewDate)}
                onChange={(values) => setReview({ ...review, reviewDate: values })}
                inputVariant="outlined"
              />
            </div>

            <div className="col-12 mt-4">
              <Typography variant="h6">Expired Date</Typography>
              <MaterialDatePicker
                label=""
                clearable
                minDate={false}
                value={IsEmpty(review.expireDate) ? null : new Date(review.expireDate)}
                onChange={(values) => setReview({ ...review, expireDate: values })}
                inputVariant="outlined"
              />
            </div>

            <div className="col-12 mt-5">
              <Typography variant="h6">Cuisine Tags</Typography>
              <Tagging
                textSize="h6"
                data={(review.cuisineTags || []).map((tag) => ({ name: tag }))}
                onChange={(values) => setReview({ ...review, cuisineTags: values })}
                limitTags={-1}
                disabled
              />
            </div>

            <div className="col-12 mt-4">
              <Typography variant="h6">Tags</Typography>
              <Tagging
                textSize="h6"
                data={(review.tags || []).map((tag) => ({ name: tag }))}
                onChange={(values) => setReview({ ...review, tags: values })}
                limitTags={-1}
                disabled
              />
            </div>

            <div className="col-12 mt-5">
              <Typography variant="h6">
                Review is on F&B
              </Typography>
              <RadioBtnSelect
                className="ml-2"
                value={review.isFnb ? 'true' : 'false'}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                onChange={(value) => {
                  const isFnb = value === 'true';
                  setReview({ ...review, isFnb, cuisineTags: isFnb ? review.cuisineTags : [] });
                }}
              />
            </div>

            <div className="col-12 mt-4">
              <div className="d-flex flex-row">
                <Typography variant="h6">
                  Link Page(s) / Location(s) to this Review
                </Typography>
                <Button
                  className="ml-3 px-4"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setEditPlaceOrLocation(null);
                    setOpenLinkPageLocationModal(true);
                  }}
                >
                  <Typography className="px-4">+ Add</Typography>
                </Button>
              </div>
            </div>

            <div className="col-12 mt-3 p-3">
              <ThemeProvider theme={themeTable}>
                <MaterialTable
                  tableRef={tableRef}
                  className="material-table"
                  title=""
                  columns={columns}
                  onChangeRowsPerPage={() => {
                      setPageSize(pageSize);
                  }}
                  data={placesAndLocations}
                  actions={[
                    {
                      icon:    'edit',
                      tooltip: 'Edit Review',
                      onClick: (event, rowData) => {
                          setEditPlaceOrLocationIndex(rowData.tableData.id);
                          setEditPlaceOrLocation(rowData);
                          setOpenLinkPageLocationModal(true);
                      },
                    },
                    {
                      icon:    'delete',
                      tooltip: 'Delete Review',
                      onClick: (event, data) => {
                askDeleteConfirmation(data);
                      },
                    },
                  ]}
                  components={{
                    Body: (data) => (
                      <>
                        <MTableBody {...data} />
                        <TableFooter>
                          <TableRow>
                            <TableCell colSpan={2} />
                            <TableCell colSpan={9}>
                              Total Applicable Outlet(s):
                              {' '}
                              {totalOutletCount}
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      </>
                    ),
                  }}
                  options={{
                    pageSize,
                    pageSizeOptions:     [10, 20, 50],
                    actionsColumnIndex:  -1,
                    emptyRowsWhenPaging: false,
                    sorting:             false,
                  }}
                />
              </ThemeProvider>
            </div>

            <div className="col-12 mt-4">
              <div className="d-flex flex-row">
                <Button
                  className="ml-3 px-4"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setEditPlaceOrLocation(null);
                    setOpenLinkPageLocationModal(true);
                  }}
                >
                  <Typography className="px-4">+ Add</Typography>
                </Button>
              </div>
            </div>

            <div className="col-12 mt-5 d-flex flex-column">
              <Typography variant="h6">Review Status</Typography>
              <RadioBtnSelect
                value={review.status}
                className="ml-2"
                options={[
                  { value: Status.Draft, label: Status.Draft, color: 'default' },
                  { value: Status.Live, label: Status.Live, color: 'secondary' },
                  { value: Status.Rejected, label: 'Reject', color: 'default' },
                ]}
                onChange={(status) => setReview({ ...review, status, remarks: '' })}
              />
            </div>

            { review.status === Status.Rejected && (
              <div className="col-12 my-4 d-flex flex-column">
                <Typography variant="h6">
                  Reject Reason
                  {' '}
                  <span style={{ color: Colours.Red }}>(*required)</span>
                </Typography>

                <p className="mt-3" style={{ fontSize: 14, color: Colours.Primary }}>
                  <b>Note:</b>
                  {' '}
                  Reject reason is mandatory if Reject status is selected
                </p>

                <TextField
                  className="mt-2"
                  value={review.remarks}
                  onChange={(e) => {
                    setReview({ ...review, remarks: e.target.value });
                  }}
                  variant="outlined"
                  multiline
                  size="small"
                  fullWidth
                />

              </div>
            )}

            <div className="col-12 mt-4 d-flex flex-row justify-content-center">
              <Button variant="contained" onClick={updateReview} color="secondary">
                <Typography className="px-4" variant="body1">Update Review</Typography>
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={openLinkPageLocationModal}
        toggle={() => setOpenLinkPageLocationModal(false)}
        size="lg"
        autoFocus={false}
      >
        <ModalHeader toggle={() => setOpenLinkPageLocationModal(false)}>Add Page / Location</ModalHeader>
        <ModalBody>
          <LinkPageOrLocation
            existingPlaces={placesAndLocations}
            editData={editPlaceOrLocation}
            reviewId={(IsEmpty(review)) ? null : review.id}
            reviewTags={(IsEmpty(review)) ? [] : _.concat(review.tags, review.cuisineTags)}
            onAdd={handleAddNewPlaceOrLocation}
            onCancel={() => setOpenLinkPageLocationModal(false)}
            onUpdate={handleUpdatePlaceOrLocation}
          />
        </ModalBody>
      </Modal>

      {
        (isLoading || isSubmitting) && <Loading />
      }
    </div>
  );
};

ReviewNew.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      reviewId: PropTypes.string.isRequired,
      pageId:   PropTypes.string.isRequired,
    }),
  }).isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  updateModalContent,
  toggleModal,
};

export default connect(null, mapDispatchToProps)(ReviewNew);
